import { Modal } from "flowbite-react"
import { FormikProvider, useFormik } from "formik"
import * as Yup from 'yup';
import ComboBoxFormik from "../../../../common/components/form/ComboBoxFormik";
import { IRenter, RENTER_STATUS } from "../../../../types/renter.type";
import ButtonWithLoader from "../../../../common/components/ui/loading/ButtonLoading";
import { getRenterById, updateRenterProps } from "../../../../common/services/renters.api";
import { useToastContext } from "../../../../common/components/ui/toast/toast.context";
import InputFormik from "../../../../common/components/form/InputFormik";
import { RenterDocumentsRow } from "../forms/fields/RenterDocumentsRow";
import { useChangeStatus } from "./fields/EditPayments/form/customHook/useChangeStatus";
import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../../../common/components/auth/authContext/AuthContext";
import { useNavigate } from "react-router-dom";

const validationSchema = Yup.object().shape({
  newStatusRenter: Yup.string().required('Campo obrigatório'),
  freezeEndDatePlanned: Yup.string().when('newStatusRenter', {
    is: (value: string) => value === RENTER_STATUS.Congelado,
    then: (schema) => schema.required('Data final do congelamento é obrigatória'),
  }),
  freezeDurationDays: Yup.number().when('newStatusRenter', {
    is: (value: string) => value === RENTER_STATUS.Congelado,
    then: (schema) => schema.required('Quantidade de dias congelado é obrigatória'),
  }),
  freezeReason: Yup.string().when('newStatusRenter', {
    is: (value: string) => value === RENTER_STATUS.Congelado,
    then: (schema) => schema.required('Motivo do congelamento é obrigatório'),
  }),

})

type Props = {
  renter: IRenter
  onClose: () => void
}

export default function ModalChangeStatus(props: Props) {
  const navigate = useNavigate();
  const { notifySuccess, notifyError } = useToastContext();
  const { auth } = useContext(AuthContext);
  const renter = props.renter;
  const [renterData, setRenterData] = useState<IRenter>();
  const disabledOption = /SV|SUA VIBE/.test(renter?.activationData?.plan?.label) ? true : false;
  const isAdmin = !(Array.isArray(auth?.user?.roles) && auth.user.roles.includes('admin')) && renter?.status == RENTER_STATUS.Blacklist;
  const isOnlyAdmin = !(Array.isArray(auth?.user?.roles) && auth.user.roles.includes('admin'))

  const { disabledForPayment, isLoading, disabledToDateRepossession, formattedDate, payments } = useChangeStatus({ renter });
  const showDebitsButton = disabledForPayment;

  useEffect(() => {
    async function loadRenterData(renterId: string) {
      if (!renterId) return;

      const result = await getRenterById(renterId)

      if (result.ok) {
        setRenterData(result.data)
      } else {
        notifyError((result as { message?: string })?.message || "unknown error")
      }

    }
    if (renter?._id) {
      loadRenterData(renter._id)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renter._id]);
  const formik = useFormik({
    initialValues: {
      newStatusRenter: '',
      freezeEndDatePlanned: '',
      freezeDurationDays: 0,
      freezeReason: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (data) => {

      if (isOnlyAdmin && renter.status === RENTER_STATUS.Blacklist)
        return notifyError("Somente administradores podem alterar o status para Blacklist");

      if (!renter._id) {
        notifyError("ID do cliente não encontrado");
        return;
      }

      const renterData = {
        _id: renter._id,
        status: data.newStatusRenter as RENTER_STATUS,
        freezeEndDatePlanned: data.freezeEndDatePlanned,
        freezeDurationDays: data.freezeDurationDays,
        freezeReason: data.freezeReason,
      }

      const result = await updateRenterProps(renterData._id, renterData)

      if (!result.ok) {
        notifyError((result as { message?: string })?.message || "unknown error")
        return
      }
      notifySuccess("Status do cliente atualizado com sucesso")
    }
  })

  const handleCloseModal = () => { props.onClose() }
  const isValidStatus =
    formik.values.newStatusRenter === RENTER_STATUS.Recision ||
    formik.values.newStatusRenter === RENTER_STATUS.WaitingRescision ||
    formik.values.newStatusRenter === RENTER_STATUS.Congelado ||
    formik.values.newStatusRenter === RENTER_STATUS.Blacklist;

  const isNewPlan = formik.values.newStatusRenter === "newPlan";

  const buttonAlreadyEnabled = formik.values.newStatusRenter === RENTER_STATUS.Congelado || formik.values.newStatusRenter === RENTER_STATUS.Blacklist;

  const renterBondVehicle = renterData?.activationData?.vehicle?.id ? true : false;

  const basicValidations =
    isLoading ||
    formik.isSubmitting ||
    !formik.values.newStatusRenter;

  const businessValidations = !renterBondVehicle && (
    (disabledToDateRepossession && !(buttonAlreadyEnabled || isNewPlan || isValidStatus)) ||
    (disabledForPayment && !(buttonAlreadyEnabled || isValidStatus || isNewPlan))
  );

  const disabledButton = basicValidations || businessValidations;


  const options = [
    { value: "", label: "-" },
    { value: RENTER_STATUS.Ativo, label: "Ativo", disabled: disabledForPayment },
    { value: RENTER_STATUS.Recision, label: "Rescisão", disabled: false },
    { value: RENTER_STATUS.Finished, label: "Finalizado", disabled: disabledForPayment },
    { value: RENTER_STATUS.Congelado, label: "Congelado", disabled: disabledOption },
    { value: RENTER_STATUS.Recall, label: "Recolhimento", disabled: disabledForPayment },
    { value: RENTER_STATUS.WaitingRescision, label: "Aguardando recisão", disabled: false },
    { value: RENTER_STATUS.ProntoParaAprovacao, label: "Pronto para aprovação", disabled: disabledForPayment },
    { value: RENTER_STATUS.PendentePagamento, label: "Pendente de pagamento", disabled: disabledForPayment },
    { value: RENTER_STATUS.ReservaPendentePagamento, label: "Reserva pendente de pagamento", disabled: disabledForPayment },
    { value: RENTER_STATUS.Blacklist, label: "Blacklist", disabled: isAdmin },
    { value: "newPlan", label: "Novo plano", }
  ]

  return (
    <>
      <Modal
        size={'4xl'}
        className='relative'
        show={true}
        onClose={() => handleCloseModal()}
      >
        <form onSubmit={formik.handleSubmit}>
          <FormikProvider value={formik}>
            <Modal.Header className="text-black">
              <h2 className="dark:text-black">Trocar o status do cliente:  {props.renter.name}</h2>
            </Modal.Header>
            <Modal.Body className='w-full relative border-gray flex flex-col text-black' style={{ minHeight: 'calc(100vh - 150px)', height: 'calc(85vh - 200px)' }}>

              {isLoading ? (
                // Exibe um indicador de carregamento enquanto verifica os pagamentos
                <div className="flex justify-center items-center h-40">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                  <p className="ml-2">Verificando pagamentos...</p>
                </div>
              ) : (
                <div>
                  <section id="validation-messages" className="mb-4">
                    {(disabledToDateRepossession && !renterBondVehicle) && (
                      <div className="mb-2">
                        <p className="text-redPrimary">O status do cliente não pode ser alterado, pois o cliente está em processo de rescisão. Data do recolhimento {formattedDate}</p>
                      </div>
                    )}
                    {disabledOption && (
                      <div className="mb-2">
                        <p className="text-redPrimary">O status do cliente não pode ser alterado para <strong>congelado</strong>, pois o plano é SUA VIBE.</p>
                      </div>
                    )}
                    {isAdmin && (
                      <div className="mb-2">
                        <p className="text-redPrimary">
                          O status do cliente não pode ser alterado para <strong>Blacklist</strong>.
                          Por favor, entre em contato com um administrador para realizar essa ação.
                        </p>

                      </div>
                    )}
                    {disabledForPayment && (
                      <div className="mb-2">
                        <p className="text-sm text-redPrimary">
                          O status do cliente não pode ser alterado!
                          <br /><br />
                          O cliente possui pagamentos <strong> pendentes, atrasados ou negativado. </strong>
                          Entrar em contato com a equipe do <strong>financeiro.</strong>
                          <br /><br />
                          Status disponível somente <strong>Rescisão</strong> e <strong>Aguardando recisão</strong>
                        </p>
                      </div>
                    )}
                  </section>
                  {(showDebitsButton && [RENTER_STATUS.Recision, RENTER_STATUS.Finished, RENTER_STATUS.WaitingRescision].includes(renter.status)) ? (
                    <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <h3 className="text-lg font-semibold text-yellow-800 mb-2">
                        Cliente com Débitos Pendentes
                      </h3>
                      <p className="text-yellow-700 mb-4">
                        Este cliente possui débitos que impedem a alteração de status.
                        Clique no botão abaixo para gerenciar os débitos.
                      </p>
                      {!(Array.isArray(auth?.user?.roles) && auth.user.roles.includes('finance')) ? (
                        <div className="p-4 bg-yellow-100 border border-yellow-200 rounded-lg">
                          <p className="text-yellow-900">
                            Você não tem permissão para gerenciar débitos. Por favor, entre em contato com o financeiro.
                          </p>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => navigate(`/clientes/${renter._id}/debitos`)}
                          className="bg-redPrimary hover:bg-red-600 text-white font-medium py-2 px-4 rounded-md transition-colors"
                        >
                          Gerenciar Débitos ({payments.length})
                        </button>
                      )}
                    </div>
                  ) : (
                    <ComboBoxFormik
                      className="mt-4"
                      label="Selecione o novo status do cliente:"
                      name="newStatusRenter"
                      options={options}
                      required={true}
                    />
                  )}
                  {formik.values.newStatusRenter === RENTER_STATUS.Congelado &&
                    <div className="grid grid-cols-1 gap-4 mt-4">
                      <InputFormik
                        label="Descreva o motivo do congelamento"
                        name="freezeReason"
                        placeholder="Motivo do congelamento"
                        required={true}
                      />

                      <InputFormik
                        label="Quantidade de dias congelado"
                        name="freezeDurationDays"
                        type="number"
                        placeholder="Dias congelado"
                        required={true}
                      />

                      <InputFormik
                        label="Data de fim do congelamento"
                        name="freezeEndDatePlanned"
                        type="date"
                        placeholder="Quantidade de dias congelado"
                        required={true}
                      />
                      {renter._id && (
                        <div className="mb-4"> {/* Margem inferior para espaçamento */}
                          <div className="div-tabs-border-line"></div>
                          {/* Adicionar altura máxima e scroll à seção de documentos */}
                          <section className="tabs-item-style max-h-[250px] overflow-y-auto">
                            <RenterDocumentsRow
                              renterId={renter._id as string}
                              currentPathRescisao={renter.rescisaoPdf}
                            />
                          </section>
                        </div>
                      )}
                    </div>
                  }
                </div>
              )}
              <div className="flex justify-end mt-4">
                <ButtonWithLoader
                  type="button"
                  disabled={disabledButton || !formik.isValid}
                  isLoading={formik.isSubmitting}
                  onClick={formik.handleSubmit}
                >
                  Salvar
                </ButtonWithLoader>

              </div>
            </Modal.Body>
          </FormikProvider>
        </form>

      </Modal>
    </>
  )
}